<script>
  import { createEventDispatcher } from 'svelte';
  import { backtestProgress, sseConnectionState } from '../../lib/sseStore.js';

  const dispatch = createEventDispatcher();

  // The run's correlation id (the channelId on the POST) and a human caption for engagement.
  export let channelId = null;
  export let strategyName = '';
  export let symbols = '';
  // The status poll saw 'queued' — the run hasn't started executing yet (review W9): say so instead
  // of an anonymous indeterminate bar.
  export let waiting = false;

  // The progress entry for THIS run (demux by channelId — concurrent runs don't cross-update).
  $: entry = channelId ? $backtestProgress.get(channelId) : null;

  // Degradation (KTD3, corrected after feasibility review): the publisher has NO replay, so events
  // emitted during a reconnect gap are LOST. Treat BOTH 'reconnecting' AND 'stale' as "fall back to
  // indeterminate" — a determinate bar would freeze then jump. A frozen percent must never imply a stall.
  $: degraded = $sseConnectionState === 'reconnecting' || $sseConnectionState === 'stale';

  // Show a determinate bar only when we have a live entry AND the connection is healthy.
  $: determinate = !!entry && !degraded;
  $: percent = entry ? Math.min(100, Math.max(0, Math.round(entry.percent))) : 0;
  $: phase = entry?.phase ?? 'progress';
  $: candleIndex = entry?.candleIndex ?? 0;
  $: totalCandles = entry?.totalCandles ?? 0;

  // Terminal-event handling (KTD3, corrected in doc review): terminal events ride a backpressure-immune
  // path (never dropped). A terminal 'failed' for the active channel is a valid trigger to leave Running
  // even before the POST returns — surface it so the shell can show the error and reconcile with the
  // eventual 500. (Completion stays POST-authoritative, so we don't act on 'completed' here.)
  let failedDispatchedFor = null;
  $: if (phase === 'failed' && channelId && failedDispatchedFor !== channelId) {
    failedDispatchedFor = channelId;
    dispatch('terminalFailed', { channelId });
  }
  // Reset the latch when the channel changes (a new run).
  $: if (channelId !== failedDispatchedFor && phase !== 'failed') {
    failedDispatchedFor = null;
  }

  // Collection phase (TASK-20260728 AC10): the run is downloading exchange history batch-by-batch
  // before the orchestrator starts — caption + count speak in batches, not candles, and every batch
  // shown is already durably cached server-side (a failure here loses nothing).
  $: collecting = phase === 'collecting';
  $: message = entry?.message ?? '';
  $: caption = waiting && !entry
    ? 'Waiting for an earlier run to finish…'
    : collecting
      ? (message ? `Collecting ${message} market data…` : 'Collecting market data…')
      : strategyName && symbols
        ? `Running ${strategyName} over ${symbols}…`
        : 'Running backtest…';
</script>

<div class="bt-progress" role="status" aria-live="polite">
  <div class="bt-progress-caption">{caption}</div>

  <div class="progress" aria-hidden="true">
    {#if determinate}
      <div class="progress-bar" style={`width:${percent}%`}></div>
    {:else}
      <div class="progress-bar indeterminate"></div>
    {/if}
  </div>

  <div class="bt-progress-meta">
    {#if determinate}
      <span class="bt-progress-percent">{percent}%</span>
      {#if totalCandles > 0}
        <span class="bt-progress-count">
          {collecting
            ? `batch ${candleIndex.toLocaleString()} of ${totalCandles.toLocaleString()}`
            : `candle ${candleIndex.toLocaleString()} of ${totalCandles.toLocaleString()}`}
        </span>
      {/if}
    {:else}
      <span class="bt-progress-count">Working… (live progress unavailable)</span>
    {/if}
  </div>
</div>

<style>
  @import './BacktestProgress.css';
</style>
